import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TimeEntry } from '../models/activity.model';
import { generateClient } from 'aws-amplify/api';
import { Schema } from '../../../../amplify/data/resource';
import { getCurrentUser } from 'aws-amplify/auth';
import { EntryFilter } from '../models/project.model';

@Injectable({
  providedIn: 'root',
})
export class TimeEntriesService {
  private _client?: ReturnType<typeof generateClient<Schema>>;

  private get client() {
    if (!this._client) {
      this._client = generateClient<Schema>();
    }
    return this._client;
  }

  private timeEntriesSubject = new BehaviorSubject<TimeEntry[]>([]);
  timeEntries$ = this.timeEntriesSubject.asObservable();
  private nextToken: string | null = null;
  public hasMoreData = false;
  private readonly PAGE_SIZE = 50;

  async resetAndLoad(filters: EntryFilter = {}) {
    this.nextToken = null;
    this.timeEntriesSubject.next([]);
    this.hasMoreData = true;
    await this.loadMoreEntries(filters);
  }

  async loadMoreEntries(filters: EntryFilter = {}) {
    if (!this.hasMoreData) return;
    try {
      const user = await getCurrentUser();
      let backendFilter: any = {};
      if (filters.employeeId) backendFilter.employeeId = { eq: filters.employeeId };
      if (filters.approvalStatus) backendFilter.approvalStatus = { eq: filters.approvalStatus };
      if (filters.startDate) {
        backendFilter.startTime = {
          between: [filters.startDate, filters.endDate ?? new Date().toISOString()]
        };
      }
      // Build OR clauses for project/task filtering
      const orClauses: any[] = [];
      // 1. Preferred: filter by projectId (UUID, exact and rename-proof)
      if (filters.projectIds?.length) {
        for (const id of filters.projectIds) {
          orClauses.push({ projectId: { eq: id } });
        }
      }
      // 2. Fallback: old entries without projectId — filter by name prefix
      if (filters.baseProjectNames?.length) {
        for (const name of filters.baseProjectNames) {
          orClauses.push({ projectName: { eq: name } });
          orClauses.push({ projectName: { beginsWith: name + ' - ' } });
        }
      }
      // 3. CRM tasks: filter by exact projectName
      if (filters.crmTaskNames?.length) {
        for (const name of filters.crmTaskNames) {
          orClauses.push({ projectName: { eq: name } });
        }
      }
      if (filters.projectNames?.length && !orClauses.length) {
        filters.projectNames.forEach(name => orClauses.push({ projectName: { eq: name } }));
      } else if (filters.projectName && !orClauses.length) {
        backendFilter.projectName = { contains: filters.projectName };
      }
      if (orClauses.length > 0) backendFilter.or = orClauses;
      else if (filters.projectId) {
        backendFilter.projectId = { eq: filters.projectId };
      }

      let accumulatedData: any[] = [];
      let token: string | null = this.nextToken;
      let hasMore = true;

      // DynamoDB filter-pagination loop: keep loading more chunks of PAGE_SIZE
      // until we either fill the current page (PAGE_SIZE items) OR there are no more records (nextToken is null)
      while (accumulatedData.length < this.PAGE_SIZE) {
        const response: any = await this.client.models.TimeEntries.list({
          limit: this.PAGE_SIZE,
          nextToken: token,
          filter: backendFilter
        });
        if (response.errors?.length) console.error('Errors loading entries', response.errors);
        
        if (response.data && response.data.length > 0) {
          accumulatedData.push(...response.data);
        }
        
        token = (response.nextToken ?? null) as string | null;
        if (!token) {
          hasMore = false;
          break;
        }
      }

      const newEntries = this.mapDataToTimeEntries(accumulatedData);
      const current = this.timeEntriesSubject.value;
      this.timeEntriesSubject.next([...current, ...newEntries]);
      this.nextToken = token;
      this.hasMoreData = hasMore;
    } catch (e) {
      console.error('Failed to load more entries', e);
    }
  }

  /** Fetches ALL entries for the given filters by looping all pages. Does not mutate shared state. */
  async fetchAllEntries(filters: EntryFilter = {}): Promise<TimeEntry[]> {
    const allEntries: TimeEntry[] = [];
    let token: string | null = null;

    const backendFilter: any = {};
    if (filters.employeeId) backendFilter.employeeId = { eq: filters.employeeId };
    if (filters.approvalStatus) backendFilter.approvalStatus = { eq: filters.approvalStatus };
    if (filters.startDate) {
      backendFilter.startTime = { between: [filters.startDate, filters.endDate ?? new Date().toISOString()] };
    }
    const orClauses: any[] = [];
    // 1. Preferred: filter by projectId (UUID, exact and rename-proof)
    if (filters.projectIds?.length) {
      for (const id of filters.projectIds) {
        orClauses.push({ projectId: { eq: id } });
      }
    }
    // 2. Fallback: old entries without projectId — filter by name prefix
    if (filters.baseProjectNames?.length) {
      for (const name of filters.baseProjectNames) {
        orClauses.push({ projectName: { eq: name } });
        orClauses.push({ projectName: { beginsWith: name + ' - ' } });
      }
    }
    // 3. CRM tasks: filter by exact projectName
    if (filters.crmTaskNames?.length) {
      for (const name of filters.crmTaskNames) {
        orClauses.push({ projectName: { eq: name } });
      }
    }
    if (filters.projectNames?.length && !orClauses.length) {
      filters.projectNames.forEach(name => orClauses.push({ projectName: { eq: name } }));
    } else if (filters.projectName && !orClauses.length) {
      backendFilter.projectName = { contains: filters.projectName };
    }
    if (orClauses.length > 0) backendFilter.or = orClauses;
    else if (filters.projectId) {
      backendFilter.projectId = { eq: filters.projectId };
    }

    while (true) {
      const response = await this.client.models.TimeEntries.list({
        limit: this.PAGE_SIZE,
        nextToken: token,
        filter: backendFilter,
      });
      if (response.errors?.length) console.error('Errors fetching entries', response.errors);
      allEntries.push(...this.mapDataToTimeEntries(response.data));
      token = (response.nextToken ?? null) as string | null;
      if (!token) break;
    }

    return allEntries;
  }

  /** Efficiently fetch ALL entries for a project using the byProjectId GSI.
   *  Pass fallbackProjectName to use a name-based scan until the GSI is deployed. */
  async fetchAllByProjectId(projectId: string, fallbackProjectName?: string): Promise<TimeEntry[]> {
    const allEntries: TimeEntry[] = [];
    let token: string | null = null;
    while (true) {
      try {
        const gsiClient = this.client.models.TimeEntries as any;
        const response: any = await gsiClient.listTimeEntriesByProjectId(
          { projectId },
          { limit: 200, nextToken: token }
        );
        if (response.errors?.length) console.error('GSI errors', response.errors);
        allEntries.push(...this.mapDataToTimeEntries(response.data ?? []));
        token = response.nextToken ?? null;
        if (!token) break;
      } catch (err) {
        // GSI not yet deployed – fall back to name-based scan
        console.warn('[TimeEntriesService] byProjectId GSI not available, falling back to name scan', err);
        if (fallbackProjectName) {
          return this.fetchAllEntries({ projectName: fallbackProjectName });
        }
        return [];
      }
    }
    return allEntries;
  }

  async loadEntriesForDate(dateStr: string) {
    try {
      const user = await getCurrentUser();
      const start = new Date(dateStr);
      start.setHours(0, 0, 0, 0);
      const end = new Date(dateStr);
      end.setHours(23, 59, 59, 999);
      const allData: any[] = [];
      let nextToken: string | null = null;
      do {
        const result: any = await this.client.models.TimeEntries.list({
          limit: 200,
          nextToken,
          filter: {
            employeeId: { eq: user.userId },
            startTime: { between: [start.toISOString(), end.toISOString()] }
          }
        });
        if (result.errors) console.error(result.errors);
        allData.push(...(result.data || []));
        nextToken = result.nextToken ?? null;
      } while (nextToken);
      const entries = this.mapDataToTimeEntries(allData);
      this.timeEntriesSubject.next(entries);
    } catch (e) {
      console.error("Failed to load day entries", e);
    }
  }

  async loadEntriesForDateRange(startDate: string, endDate: string) {
    try {
      const user = await getCurrentUser();
      const allData: any[] = [];
      let nextToken: string | null = null;
      do {
        const result: any = await this.client.models.TimeEntries.list({
          limit: 200,
          nextToken,
          filter: {
            employeeId: { eq: user.userId },
            startTime: { between: [startDate, endDate] }
          }
        });
        if (result.errors) console.error(result.errors);
        allData.push(...(result.data || []));
        nextToken = result.nextToken ?? null;
      } while (nextToken);
      const entries = this.mapDataToTimeEntries(allData);
      this.timeEntriesSubject.next(entries);
    } catch (e) {
      console.error("Failed to load date range entries", e);
    }
  }

  /**
   * Build and persist an entry from the entry-form payload. Lives here rather than
   * in a host component so the form has a single save path whatever renders it.
   * `fallbackStart`/`fallbackEnd` cover a payload that omitted the edited times.
   */
  async createFromEntryPayload(data: any, fallbackStart: Date, fallbackEnd: Date) {
    const startTime: Date = data.startTime instanceof Date ? data.startTime : fallbackStart;
    const endTime: Date = data.endTime instanceof Date ? data.endTime : fallbackEnd;
    if (!startTime || !endTime) return;

    // projectName is a composed label; guard against an object slipping through.
    let projectName = data.projectName;
    if (typeof projectName === 'object' && projectName !== null) {
      projectName = projectName.projectName || String(projectName);
    }

    await this.createTimeEntries({
      startTime,
      endTime,
      projectName,
      projectId: data.projectId ?? null,
      stageId: data.stageId ?? null,
      content: data.description || data.content,
      imagePaths: data.imagePaths,
      // Previously dropped here, so un-ticking "billable" had no effect.
      billable: data.billable,
    });
  }

  async createTimeEntries(timeEntry: TimeEntry) {
    try {
      const user = await getCurrentUser();
      const entries = {
        ...timeEntry,
        employeeId: user.userId,
        startTime: timeEntry.startTime.toISOString(),
        endTime: timeEntry.endTime.toISOString(),
        imagePaths: timeEntry.imagePaths || [],
        billable: timeEntry.billable ?? true,
        approvalStatus: 'DRAFT' as const,
      };
      const { data, errors } = await this.client.models.TimeEntries.create(entries);
      if (errors && errors.length) console.error('Errors creating', errors);
      const newEntry = this.mapDataToTimeEntries([data])[0];
      const current = this.timeEntriesSubject.value;
      this.timeEntriesSubject.next([...current, newEntry]);
    } catch (e) {
      console.error('Failed creating time entries', e);
    }
  }

  async updateTimeEntry(timeEntryId: string, updates: Partial<TimeEntry>) {
    try {
      const payload: any = { ...updates };
      delete payload.lane;
      delete payload.color;
      if (payload.startTime instanceof Date) payload.startTime = payload.startTime.toISOString();
      if (payload.endTime instanceof Date) payload.endTime = payload.endTime.toISOString();
      const { data, errors } = await this.client.models.TimeEntries.update({
        id: timeEntryId,
        ...payload,
      });
      if (errors?.length) {
        console.error('Errors updating entry', errors);
        return null;
      }
      const updated = this.mapDataToTimeEntries([data])[0];
      const current = this.timeEntriesSubject.value.map(e => e.id === updated.id ? updated : e);
      this.timeEntriesSubject.next(current);
      return data;
    } catch (e) {
      console.error('Failed update', e);
      return null;
    }
  }

  async deleteTimeEntry(timeEntryId: string) {
    try {
      const { data, errors } = await this.client.models.TimeEntries.delete({ id: timeEntryId });
      if (errors) return null;
      const current = this.timeEntriesSubject.value.filter(entry => entry.id !== timeEntryId);
      this.timeEntriesSubject.next(current);
      return data;
    } catch (e) {
      console.error("Failed delete", e);
      return null;
    }
  }

  async addImagesToEntry(timeEntryId: string, newPaths: string[]) {
    const entry = this.timeEntriesSubject.value.find(e => e.id === timeEntryId);
    if (!entry) return;
    const updatedPaths = [...(entry.imagePaths || []), ...newPaths];
    await this.updateTimeEntry(timeEntryId, { imagePaths: updatedPaths });
  }

  async removeImageFromEntry(timeEntryId: string, pathToRemove: string) {
    const entry = this.timeEntriesSubject.value.find(e => e.id === timeEntryId);
    if (!entry) return;
    const updatedPaths = (entry.imagePaths ?? []).filter(p => p !== pathToRemove);
    await this.updateTimeEntry(timeEntryId, { imagePaths: updatedPaths });
  }


  async approveEntry(id: string) {
    return this.updateTimeEntry(id, { approvalStatus: 'APPROVED' });
  }

  async setDraftEntry(id: string) {
    return this.updateTimeEntry(id, { approvalStatus: 'DRAFT' });
  }

  private mapDataToTimeEntries(data: any[]): TimeEntry[] {
    return data.map(te => ({
      id: te.id,
      content: te.content,
      imagePaths: te.imagePaths || [],
      projectName: te.projectName,
      projectId: te.projectId ?? null,
      stageId: te.stageId ?? null,
      employeeId: te.employeeId,
      startTime: new Date(te.startTime),
      endTime: new Date(te.endTime),
      billable: te.billable ?? true,
      approvalStatus: te.approvalStatus ?? 'DRAFT',
    }));
  }
}
